// src/pages/User/ArchiveFolderView.jsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import NoteCard from "../../components/NoteCard";
import SwiftAlert from "../../components/SwiftAlert";
import ShareModal from "../../components/User/ShareModal";
import {
  getFolderFiles,
  getArchivedFolders,
  updateReviewer,
  deleteReviewer,
  getReviewerById,
} from "../../services/api";
import { checkEnabled, computeVerified, normalizeColorKey } from "../../utils/libraryUtils";
import { generatePDFDocument } from "../../utils/pdfGenerator";

const FOLDER_THEME_VARIANTS = {
  blue: { iconBg: "bg-blue-100", iconText: "text-blue-600" },
  orange: { iconBg: "bg-orange-100", iconText: "text-orange-600" },
  purple: { iconBg: "bg-purple-100", iconText: "text-purple-600" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600" },
  red: { iconBg: "bg-red-100", iconText: "text-red-600" },
  gray: { iconBg: "bg-slate-100", iconText: "text-slate-600" },
};

const LIST_ITEM_VARIANTS = {
  blue:    { iconBg: "bg-blue-100",    iconText: "text-blue-600",    activeBorder: "border-blue-500",    activeBg: "bg-blue-50",    ring: "ring-blue-500" },
  orange:  { iconBg: "bg-orange-100",  iconText: "text-orange-600",  activeBorder: "border-orange-500",  activeBg: "bg-orange-50",  ring: "ring-orange-500" },
  purple:  { iconBg: "bg-purple-100",  iconText: "text-purple-600",  activeBorder: "border-purple-500",  activeBg: "bg-purple-50",  ring: "ring-purple-500" },
  emerald: { iconBg: "bg-emerald-100", iconText: "text-emerald-600", activeBorder: "border-emerald-500", activeBg: "bg-emerald-50", ring: "ring-emerald-500" },
  red:     { iconBg: "bg-red-100",     iconText: "text-red-600",     activeBorder: "border-red-500",     activeBg: "bg-red-50",     ring: "ring-red-500" },
  gray:    { iconBg: "bg-slate-100",   iconText: "text-slate-600",   activeBorder: "border-slate-500",   activeBg: "bg-slate-50",   ring: "ring-slate-500" },
};

const ArchiveFolderView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const folderFromState = location.state?.folder;
  const [folderMeta, setFolderMeta] = useState(folderFromState || null);
  const folderId = params.folderId || params.id || folderFromState?._id || folderFromState?.id;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [archivedFolders, setArchivedFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  // Move Modal State
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [noteToMove, setNoteToMove] = useState(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);

  // Share Modal State
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);

  const folderName = folderMeta?.name || "Archived Folder";

  const folderTheme = useMemo(() => {
    const key = normalizeColorKey(folderMeta?.color);
    return FOLDER_THEME_VARIANTS[key] || FOLDER_THEME_VARIANTS.blue;
  }, [folderMeta?.color]);

  const q = useMemo(() => {
    return (new URLSearchParams(location.search).get("q") || "").toLowerCase().trim();
  }, [location.search]);

  // Fetch Folder Files and Archived Folders
  const fetchContent = useCallback(async () => {
    if (!folderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [filesRes, foldersRes] = await Promise.all([
        getFolderFiles(folderId),
        getArchivedFolders(),
      ]);

      const filesArr = Array.isArray(filesRes) ? filesRes : filesRes?.files ?? [];
      const enriched = filesArr.map((f) => ({ ...f, verified: computeVerified(f) }));
      setFiles(enriched);

      const foldersArr = Array.isArray(foldersRes) ? foldersRes : foldersRes?.data ?? [];
      setArchivedFolders(foldersArr);

      // If folder metadata wasn't passed via location.state, find it from foldersRes
      if (!folderMeta) {
        const found = foldersArr.find((f) => String(f._id) === String(folderId));
        if (found) {
          setFolderMeta(found);
        } else if (filesRes?.folder) {
          setFolderMeta(filesRes.folder);
        }
      }
    } catch (err) {
      console.error("[ArchiveFolderView] Failed to load folder files:", err);
      setAlert({ type: "error", title: "Failed", message: "Could not load archived folder." });
    } finally {
      setLoading(false);
    }
  }, [folderId, folderMeta]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Filtering
  const filteredFiles = useMemo(() => {
    if (!q) return files;
    return files.filter((f) => {
      const title = (f.title || f.topic || "").toLowerCase();
      const meta = (f.meta || "").toLowerCase();
      return title.includes(q) || meta.includes(q);
    });
  }, [files, q]);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);

  // --- Handlers: Note Actions ---

  const handleStudy = (note, requestedMode) => {
    const cfg = note.config || {};
    let mode = requestedMode;
    if (!mode) {
      if (checkEnabled(cfg.summaryEnabled)) mode = "summary";
      else if (checkEnabled(cfg.flashcardsEnabled)) mode = "flashcards";
      else if (checkEnabled(cfg.quizEnabled)) mode = "quiz";
    }
    if (!mode) return;

    navigate("/study-mode", {
      state: {
        mode,
        noteData: {
          ...note,
          _id: note?._id,
          id: note?._id || note?.id,
          title: note?.title || note?.topic || "Untitled",
          subject: folderName,
          hasSummary: checkEnabled(cfg.summaryEnabled),
          hasFlashcards: checkEnabled(cfg.flashcardsEnabled),
          hasQuiz: checkEnabled(cfg.quizEnabled),
          config: cfg,
        },
        reviewerId: note._id || note.id,
      },
    });
  };

  const handleRenameNote = async (note, newTitle) => {
    const cleanTitle = newTitle.trim();
    if (!cleanTitle) return;

    const isDuplicate = files.some(
      (f) =>
        (f.title || f.topic || "").trim().toLowerCase() === cleanTitle.toLowerCase() &&
        f._id !== note._id
    );

    if (isDuplicate) {
      setAlert({
        type: "warning",
        title: "Name Taken",
        message: `A note named "${cleanTitle}" already exists in this folder.`,
      });
      return;
    }

    try {
      setFiles((prev) =>
        prev.map((f) => (f._id === note._id ? { ...f, title: cleanTitle, topic: cleanTitle } : f))
      );
      await updateReviewer(note._id, { title: cleanTitle });
      setAlert({ type: "success", title: "Renamed", message: "Note renamed successfully." });
    } catch (error) {
      console.error("[ArchiveFolderView] Rename failed:", error);
      setAlert({ type: "error", title: "Failed", message: error?.message || "Failed to rename note." });
    }
  };

  const handleColorChange = async (note, newColor) => {
    try {
      setFiles((prev) =>
        prev.map((f) => (f._id === note._id ? { ...f, color: newColor } : f))
      );
      await updateReviewer(note._id, { color: newColor });
    } catch (error) {
      console.error("[ArchiveFolderView] Color update failed:", error);
      setAlert({ type: "error", title: "Failed", message: "Failed to update color." });
    }
  };

  // Move Logic (Within Archive: To another archived folder or to No Folder)
  const openMoveModal = (note) => {
    setNoteToMove(note);
    setIsMoveModalOpen(true);
  };

  const handleMoveSubmit = async (targetFolderId) => {
    if (!noteToMove) return;

    if (String(folderId) === String(targetFolderId || "")) {
      setIsMoveModalOpen(false);
      setNoteToMove(null);
      return;
    }

    const noteId = noteToMove._id || noteToMove.id;
    const noteTitle = noteToMove.title || noteToMove.topic || "Note";

    try {
      // Remove from current folder view
      setFiles((prev) => prev.filter((f) => (f._id || f.id) !== noteId));
      setIsMoveModalOpen(false);
      setNoteToMove(null);

      const targetFolder = archivedFolders.find((f) => String(f._id) === String(targetFolderId));
      setAlert({
        type: "success",
        title: "Moved",
        message: targetFolder
          ? `"${noteTitle}" moved to "${targetFolder.name}".`
          : `"${noteTitle}" moved to standalone archive (No Folder).`,
      });

      await updateReviewer(noteId, { folderId: targetFolderId });
    } catch (error) {
      console.error("[ArchiveFolderView] Move failed:", error);
      setAlert({ type: "error", title: "Failed", message: "Failed to move note." });
    }
  };

  // Unarchive Logic: restores note to Library
  const handleUnarchiveNote = async (note) => {
    const noteId = note._id || note.id;
    try {
      setFiles((prev) => prev.filter((f) => (f._id || f.id) !== noteId));
      await updateReviewer(noteId, { archived: false });
      setAlert({
        type: "success",
        title: "Unarchived",
        message: `"${note.title || note.topic}" restored to Library.`,
      });
    } catch (error) {
      console.error("[ArchiveFolderView] Unarchive failed:", error);
      setAlert({ type: "error", title: "Failed", message: "Failed to unarchive note." });
    }
  };

  // Delete Logic
  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteSubmit = async () => {
    if (!noteToDelete) return;
    const noteId = noteToDelete._id || noteToDelete.id;
    try {
      setFiles((prev) => prev.filter((f) => (f._id || f.id) !== noteId));
      setIsDeleteModalOpen(false);
      setNoteToDelete(null);
      setAlert({ type: "success", title: "Deleted", message: "Note deleted permanently." });
      await deleteReviewer(noteId);
    } catch (error) {
      console.error("[ArchiveFolderView] Delete error:", error);
      setAlert({ type: "error", title: "Failed", message: "Failed to delete note." });
    }
  };

  // PDF Download
  const handleDownloadPdf = async (note) => {
    const reviewerId = note?._id || note?.id;
    if (!reviewerId) {
      setAlert({ type: "error", title: "Failed", message: "Reviewer ID is missing." });
      return;
    }

    try {
      const raw = await getReviewerById(reviewerId);
      const normalized = raw && raw.data && raw.status ? raw.data : raw;
      const meta = normalized?.meta || normalized?.data?.meta || {};
      const data =
        normalized?.meta && normalized?.data
          ? normalized.data
          : normalized?.data?.data ?? normalized?.data ?? normalized;

      const summary =
        (typeof data === "string" ? data : "") ||
        data?.summaryHtml ||
        data?.summary ||
        data?.html ||
        data?.content ||
        "";

      const flashcardsRaw = data?.flashcards || data?.flashCards || data?.cards || [];
      const flashcards = Array.isArray(flashcardsRaw)
        ? flashcardsRaw
            .map((c) => ({
              front: c?.front || c?.term || c?.question || c?.q || c?.title || "",
              back: c?.back || c?.definition || c?.answer || c?.a || c?.content || "",
            }))
            .filter((c) => c.front || c.back)
        : [];

      const quizRaw = data?.quiz || data?.questions || [];
      const quiz = Array.isArray(quizRaw)
        ? quizRaw
            .map((q) => {
              const optsRaw = q?.options || q?.choices || q?.answers || [];
              const options = Array.isArray(optsRaw)
                ? optsRaw
                    .map((o) => (typeof o === "string" ? o : o?.text || o?.label || ""))
                    .filter(Boolean)
                : [];
              return {
                question: q?.question || q?.prompt || q?.q || "",
                options,
                answer: q?.answer || q?.correctAnswer || q?.correct || "",
                explanation: q?.explanation || q?.reason || "",
              };
            })
            .filter((q) => q.question)
        : [];

      if (!summary && flashcards.length === 0 && quiz.length === 0) {
        setAlert({
          type: "warning",
          title: "No Content",
          message: "This reviewer has no downloadable content yet.",
        });
        return;
      }

      const cfg = { ...(meta?.config || {}), ...(note?.config || {}) };
      generatePDFDocument({
        title: note?.title || note?.topic || meta?.topic || "Reviewer",
        generatedData: { summary, flashcards, quiz },
        config: {
          ...cfg,
          summaryEnabled: checkEnabled(cfg.summaryEnabled),
          flashcardsEnabled: checkEnabled(cfg.flashcardsEnabled),
          quizEnabled: checkEnabled(cfg.quizEnabled),
        },
      });
    } catch (error) {
      setAlert({
        type: "error",
        title: "Failed",
        message: error?.message || "Failed to download PDF.",
      });
    }
  };

  // Share Modal
  const openShareModal = (note) => {
    setNoteToShare(note);
    setIsShareModalOpen(true);
  };

  const handleShareModalUpdateNote = (updatedNote) => {
    setFiles((prev) =>
      prev.map((f) =>
        String(f._id || f.id) === String(updatedNote._id || updatedNote.id) ? updatedNote : f
      )
    );
  };

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden text-left">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} title="Archive" />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto w-full animate-fade-in pb-20">

            {/* Breadcrumb & Header Banner */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6 mb-8">
              <div>
                <nav className="flex items-center text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">
                  <span
                    className="cursor-pointer hover:text-blue-600 transition flex items-center gap-1"
                    onClick={() => navigate("/library/archive")}
                  >
                    <i className="fa-solid fa-box-archive text-xs"></i>
                    Archive
                  </span>
                  <i className="fa-solid fa-chevron-right mx-2 text-[8px]"></i>
                  <span className="text-slate-600 truncate max-w-xs">{folderName}</span>
                </nav>

                <div className="flex items-center gap-4 text-left">
                  <div
                    className={`w-12 h-12 rounded-xl ${folderTheme.iconBg} ${folderTheme.iconText} flex items-center justify-center text-xl shadow-sm`}
                  >
                    <i className="fa-solid fa-folder"></i>
                  </div>

                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-2xl font-bold text-slate-900 leading-none mb-1">
                        {folderName}
                      </h1>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100 uppercase tracking-wider">
                        Archived
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">
                      {loading
                        ? "Loading..."
                        : `${filteredFiles.length} ${q ? "matched files" : "Archived Files"}`}
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate("/library/archive")}
                className="bg-white text-slate-700 border border-slate-200 px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50 shadow-sm transition flex items-center justify-center gap-2"
              >
                <i className="fa-solid fa-arrow-left"></i> Back to Archive
              </button>
            </div>

            {/* Content Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-48 bg-white rounded-xl border border-gray-100 animate-pulse shadow-sm" />
                ))}
              </div>
            ) : filteredFiles.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center shadow-sm">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-folder-open text-blue-500 text-2xl"></i>
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-1">
                  {q ? "No notes found" : "Folder is Empty"}
                </h3>
                <p className="text-sm text-slate-400 mb-6">
                  {q
                    ? "Try a different keyword."
                    : "There are no archived notes in this folder. You can move archived notes here from the Archive."}
                </p>
                <button
                  onClick={() => navigate("/library/archive")}
                  className="bg-blue-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition shadow-md"
                >
                  <i className="fa-solid fa-arrow-left mr-2"></i> Return to Archive
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredFiles.map((file) => (
                  <NoteCard
                    key={file._id || file.id}
                    note={file}
                    folderName={folderName}
                    showVerified={false}
                    onStudy={handleStudy}
                    onRename={handleRenameNote}
                    onColorChange={handleColorChange}
                    onMove={openMoveModal}
                    onUnarchive={handleUnarchiveNote}
                    onDelete={openDeleteModal}
                    onDownloadPdf={handleDownloadPdf}
                    onShare={openShareModal}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* MOVE NOTE MODAL (Inside Archive Only) */}
      {isMoveModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Move to Archive Folder</h3>
            <p className="text-sm text-gray-500 mb-4">
              Select an archived destination for <b>{noteToMove?.title || noteToMove?.topic}</b>
            </p>

            <div className="max-h-60 overflow-y-auto space-y-2 mb-4 pr-1">
              {/* Option: Remove from folder -> Standalone in Archive */}
              <button
                onClick={() => handleMoveSubmit(null)}
                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500">
                  <i className="fa-solid fa-folder-minus"></i>
                </div>
                <div>
                  <span className="font-semibold text-gray-700 block">No Folder</span>
                  <span className="text-[11px] text-gray-400">Standalone note in Archive</span>
                </div>
              </button>

              {/* Archived folders only */}
              {archivedFolders.map((f) => {
                const isCurrent = String(folderId) === String(f._id);
                const theme = LIST_ITEM_VARIANTS[normalizeColorKey(f.color)] || LIST_ITEM_VARIANTS.blue;

                return (
                  <button
                    key={f._id}
                    onClick={() => handleMoveSubmit(f._id)}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition flex items-center gap-3 ${
                      isCurrent
                        ? `${theme.activeBorder} ${theme.activeBg} ring-1 ${theme.ring}`
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${theme.iconBg} ${theme.iconText}`}>
                      <i className="fa-solid fa-folder"></i>
                    </div>
                    <span className="font-semibold text-gray-700 truncate">{f.name}</span>
                    {isCurrent && (
                      <span className="ml-auto text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setIsMoveModalOpen(false)}
              className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* DELETE NOTE MODAL */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xs w-full p-6 animate-in zoom-in-95 duration-200 text-center">
            <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
              <i className="fa-solid fa-trash-can"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Note?</h3>
            <p className="text-gray-500 text-sm mb-6">
              Are you sure you want to permanently delete{" "}
              <b>{noteToDelete?.title || noteToDelete?.topic}</b>? This cannot be undone.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleDeleteSubmit}
                className="w-full py-2.5 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 transition shadow-md"
              >
                Yes, Delete it
              </button>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="w-full py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SHARE MODAL */}
      {isShareModalOpen && noteToShare && (
        <ShareModal
          note={noteToShare}
          onClose={() => {
            setIsShareModalOpen(false);
            setNoteToShare(null);
          }}
          onUpdateNote={handleShareModalUpdateNote}
          onAlert={setAlert}
        />
      )}

      {/* ALERT */}
      {alert && (
        <SwiftAlert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </div>
  );
};

export default ArchiveFolderView;
